import * as React from "react";
import {ReactNode, useEffect, useState} from "react";
import {Controller, useFormContext} from "react-hook-form";
import {Box, CheckboxProps, FormControlLabel} from "@mui/material";
import {StyledCheckbox, StyledTypography} from "../providers/layout/LayoutProvider";

export type FormInputCheckboxProps = Omit<CheckboxProps, 'name'> & {
   name: string;
   label: string;
   setValue?: any;
   errorMessage?: string
   handleError?: boolean
   onTextClick?: (text: string) => boolean
}

export const FormInputCheckbox = ({
                                     name,
                                     label,
                                     errorMessage,
                                     handleError,
                                     onTextClick,
                                     ...rest
                                  }: FormInputCheckboxProps) => {
   const methods = useFormContext();
   const {control} = methods;
   return (
      <Controller
         name={name}
         control={control}
         render={({
                     field: {onChange, value}
                  }) => (
            <FormInputCheckboxCore
               onTextClick={onTextClick}
               name={name} label={label} onChange={onChange} checked={!!value} {...rest}/>
         )}
      />
   );
};

type FormInputCheckboxCoreProps = Omit<CheckboxProps, 'name'> & {
   name: string
   label: string
   helperText?: string
   onTextClick?: (text: string) => boolean
}

export const WildCardRegExp = new RegExp(/{{([\w\W\d\D\s\S]+?)}}/g)

export const FormInputCheckboxCore = ({name, label, onTextClick, ...rest}: FormInputCheckboxCoreProps) => {
   const [shown, setShown] = useState<number | undefined>(undefined)
   const [labels, setLabels] = useState<React.ReactNode>(null)

   useEffect(() => {
      const elements: ReactNode[] = []
      const matches = Array.from(label?.matchAll(WildCardRegExp) || []);
      if (matches.length > 0) {
         let startIndex: number = 0;

         for (const match of matches) {
            const prefix = match.input?.substring(startIndex, match.index);
            if (prefix) {
               elements.push(
                  <StyledTypography key={'prefix_' + startIndex} component={"span"} sx={{marginTop: 1}}
                                    variant={"body1"}
                                    dangerouslySetInnerHTML={{__html: prefix}}/>
               )
            }
            const matchContent = match[1].split('::')
            if (matchContent.length === 2) {
               elements.push(<>{shown !== match.index &&
                     <a href={"#/"} onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (onTextClick && onTextClick(matchContent[1])) {
                           return;
                        }
                        setShown(match.index);
                     }}>
                        <StyledTypography key={'link_' + startIndex} component={"span"}
                                          sx={{marginTop: 1, textDecoration: 'underline'}}
                                          variant={"body1"} dangerouslySetInnerHTML={{__html: matchContent[0]}}/>
                     </a>
                  }
                  </>
               )
               elements.push(<>{shown === match.index &&
                     <StyledTypography key={'disclosure_' + startIndex} component={"span"} sx={{marginTop: 1}}
                                       variant={"body1"} dangerouslySetInnerHTML={{__html: matchContent[1]}}/>
                  }
                  </>
               )
            }
            startIndex = (match.index || 0) + match[0].length
         }
      } else {
         elements.push(
            <StyledTypography component={"span"} key={'main'}
                              variant={"body1"}
                              dangerouslySetInnerHTML={{__html: label}}/>
         )
      }
      setLabels(<Box sx={{marginTop: 1}}>{elements}</Box>);
   }, [shown, label, onTextClick])

   if (!labels) {
      return null
   }

   return <FormControlLabel control={
      <StyledCheckbox
         id={name}
         name={name}
         size="medium"
         {...rest}
      />
   } sx={{alignItems: 'flex-start'}} label={labels}/>
}